import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, DollarSign, AlertCircle } from 'lucide-react';
import { useUser } from '../../store/useAuthStore';
import { apiClient } from '../../lib/api';
import type { RequestType } from '../../types/productRequest';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';

interface ProductRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (request: CreateProductRequest) => void;
  relatedProductId?: string; // If requesting variant of existing product
}

export interface CreateProductRequest {
  requestTitle: string;
  requestDescription: string;
  requestType: RequestType;
  desiredPrice?: number;
  quantityNeeded?: number;
  relatedProductId?: string;
  // suggestedDetails will be added in future iterations
}

interface FormData {
  requestTitle: string;
  requestDescription: string;
  requestType: RequestType;
  desiredPrice: string;
  quantityNeeded: string;
}

interface FormErrors {
  requestTitle?: string;
  desiredPrice?: string;
  quantityNeeded?: string;
}

export const ProductRequestModal: React.FC<ProductRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  relatedProductId
}) => {
  const user = useUser();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    requestTitle: '',
    requestDescription: '',
    requestType: relatedProductId ? 'product_variant' : 'new_product',
    desiredPrice: '',
    quantityNeeded: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ESC key to close modal
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  const getRoleSpecificMessage = () => {
    if (user?.role === 'physician' || user?.role === 'physician_readonly') {
      return "Request a product that you'd like pharmacies to stock";
    } else if (user?.role === 'distributor') {
      return "Request a product that you'd like to offer";
    }
    return "Request a new product";
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.requestTitle.trim()) {
      newErrors.requestTitle = 'Product name is required';
    }

    if (formData.desiredPrice && parseFloat(formData.desiredPrice) < 0) {
      newErrors.desiredPrice = 'Price must be positive';
    }

    if (formData.quantityNeeded && parseInt(formData.quantityNeeded) < 1) {
      newErrors.quantityNeeded = 'Quantity must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !user) return;

    setIsSubmitting(true);

    try {
      const requestData = {
        requestTitle: formData.requestTitle.trim(),
        requestDescription: formData.requestDescription.trim(),
        requestType: formData.requestType,
        desiredPrice: formData.desiredPrice ? parseFloat(formData.desiredPrice) : undefined,
        quantityNeeded: formData.quantityNeeded ? parseInt(formData.quantityNeeded) : undefined,
        relatedProductId: relatedProductId
      };

      // Make actual API call
      await apiClient.createProductRequest(requestData);

      // Call onSuccess if provided
      if (onSuccess) {
        onSuccess(requestData);
      }

      // Show success message
      setShowSuccess(true);

      // Reset form
      setFormData({
        requestTitle: '',
        requestDescription: '',
        requestType: relatedProductId ? 'product_variant' : 'new_product',
        desiredPrice: '',
        quantityNeeded: ''
      });
      setErrors({});

      // Navigate to requests page after showing success
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        navigate('/requests');
      }, 1500);

    } catch (error) {
      console.error('Error submitting product request:', error);
      // Set form error for user feedback
      setErrors({ requestTitle: error instanceof Error ? error.message : 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBlur = (field: keyof FormData) => {
    // Validate on blur
    const newErrors: FormErrors = {};

    if (field === 'desiredPrice' && formData.desiredPrice && parseFloat(formData.desiredPrice) < 0) {
      newErrors.desiredPrice = 'Price must be positive';
    }

    if (field === 'quantityNeeded' && formData.quantityNeeded && parseInt(formData.quantityNeeded) < 1) {
      newErrors.quantityNeeded = 'Quantity must be at least 1';
    }

    setErrors(prev => ({
      ...prev,
      ...newErrors
    }));
  };

  const isFormValid = formData.requestTitle.trim().length > 0;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="modal-overlay"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Request a Product</h2>
                <p className="text-blue-100 text-sm mt-1">
                  {getRoleSpecificMessage()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-blue-600 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center text-green-800">
              <AlertCircle className="w-5 h-5 mr-2" />
              <p className="font-medium">Request submitted successfully!</p>
            </div>
            <p className="text-sm text-green-700 mt-1">
              We'll review your request and get back to you soon.
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Product Name */}
          <div>
            <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-2">
              Product Name *
            </label>
            <input
              id="productName"
              type="text"
              value={formData.requestTitle}
              onChange={(e) => handleChange('requestTitle', e.target.value)}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.requestTitle ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Tirzepatide 5mg/ml Injectable"
            />
            {errors.requestTitle && (
              <p className="mt-1 text-sm text-red-600">{errors.requestTitle}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.requestDescription}
              onChange={(e) => handleChange('requestDescription', e.target.value)}
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Tell us more about the product you're looking for..."
            />
            <p className="mt-1 text-sm text-gray-500">
              Include details like concentration, format, size, or any special requirements
            </p>
          </div>

          {/* Price and Quantity Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Desired Price */}
            <div>
              <label htmlFor="desiredPrice" className="block text-sm font-medium text-gray-700 mb-2">
                Desired Price
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="desiredPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.desiredPrice}
                  onChange={(e) => handleChange('desiredPrice', e.target.value)}
                  onBlur={() => handleBlur('desiredPrice')}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.desiredPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
              </div>
              {errors.desiredPrice && (
                <p className="mt-1 text-sm text-red-600">{errors.desiredPrice}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Optional: What price would you like to buy this at?
              </p>
            </div>

            {/* Quantity Needed */}
            <div>
              <label htmlFor="quantityNeeded" className="block text-sm font-medium text-gray-700 mb-2">
                Quantity Needed
              </label>
              <input
                id="quantityNeeded"
                type="number"
                min="1"
                value={formData.quantityNeeded}
                onChange={(e) => handleChange('quantityNeeded', e.target.value)}
                onBlur={() => handleBlur('quantityNeeded')}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.quantityNeeded ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="1"
              />
              {errors.quantityNeeded && (
                <p className="mt-1 text-sm text-red-600">{errors.quantityNeeded}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Optional: How many would you like to order?
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Package className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

